// src/components/SimulationSection.tsx - Refactored with shared utilities
import React, { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { LoadingState, SimulationResult } from '../types/types';
import { simulateTournament } from '../api/api';
import ResultsDisplay from './ResultsDisplay';
import { useConfig } from '../context/ConfigContext';
import { useAuth } from '../context/AuthContext';
import { isBracketComplete } from '../utils/bracketUtils';
import { useSimulationCache, buildCacheKey } from '../hooks/useSimulationCache';
import { formatLargeNumber } from '../utils/formatUtils';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import InfoTooltip from './InfoTooltip';
import ProUpgradeModal from './ProUpgradeModal';
import ChancesOverTime from './ChancesOverTime';
import { trackEvent } from '../utils/analytics';

interface SimulationSectionProps {
  playerPicks: string;
  knownWinners: string[];
  onPlayerSelect: (playerName: string) => void;
  pinnedPlayers?: string[];
  onPinnedPlayersChange?: (players: string[]) => void;
  pageSize?: number;
  activePoolName?: string | null;
}

const SimulationSection: React.FC<SimulationSectionProps> = ({
  playerPicks,
  knownWinners,
  onPlayerSelect,
  pinnedPlayers,
  onPinnedPlayersChange,
  pageSize,
  activePoolName
}) => {
  const [loadingState, setLoadingState] = useState<LoadingState>('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [simulationResults, setSimulationResults] = useState<SimulationResult | null>(null);
  const [showProModal, setShowProModal] = useState(false);
  const [showChancesDialog, setShowChancesDialog] = useState(false);
  const { isPro } = useAuth();

  // Track last simulated values for change detection
  const [lastSimulatedPicks, setLastSimulatedPicks] = useState<string>('');
  const [lastSimulatedWinners, setLastSimulatedWinners] = useState<string[]>([]);
  const [lastSimulatedConfig, setLastSimulatedConfig] = useState<string>('');
  const [changesMade, setChangesMade] = useState<boolean>(true);

  // Get the configuration from context
  const { roundConfigs, currentPointValues } = useConfig();

  // LRU cache for simulation results
  const simulationCache = useSimulationCache();

  // Fixed sample size as per requirements
  const sampleSize = 10000;

  const bracketComplete = isBracketComplete(knownWinners);

  // Count actual known winners
  const actualKnownWinnersCount = knownWinners.filter(winner =>
    winner && winner.trim() !== "" && winner !== "xxx"
  ).length;

  // Create a serializable version of config for change detection
  const currentConfigString = JSON.stringify({ roundConfigs, currentPointValues });

  // Enhanced change detection - clears results for picks/config changes, reactivates button for all changes
  useEffect(() => {
    // Compare current values with last simulated values
    const picksChanged = playerPicks !== lastSimulatedPicks;
    const winnersChanged = JSON.stringify(knownWinners) !== JSON.stringify(lastSimulatedWinners);
    const configChanged = currentConfigString !== lastSimulatedConfig;

    if (picksChanged || winnersChanged || configChanged) {
      // Always reactivate the button when any changes are detected
      setChangesMade(true);

      // Only clear results for player picks or configuration changes (not bracket changes)
      if ((picksChanged || configChanged) && simulationResults) {
        setSimulationResults(null);
      }
    }
  }, [playerPicks, knownWinners, currentConfigString, lastSimulatedPicks, lastSimulatedWinners, lastSimulatedConfig, simulationResults]);

  const handleSimulate = async () => {
    // Check pro status before simulating
    if (!isPro) {
      setShowProModal(true);
      return;
    }

    // Validate inputs
    if (!playerPicks.trim()) {
      setErrorMessage('Please enter player picks before simulating.');
      return;
    }

    try {
      setErrorMessage('');

      // Extract round names and point values from config
      const roundNames = [
        "Round of 64",  // First round (not scored)
        ...roundConfigs.map(config => config.name)  // Next 5 rounds from config
      ];

      // Use current point values from context (comes from pool configuration)
      const pointsPerRound = [...currentPointValues];

      // Check LRU cache before calling API
      const cacheKey = buildCacheKey(playerPicks, knownWinners, pointsPerRound, roundNames);
      const cachedResult = simulationCache.get(cacheKey);

      if (cachedResult) {
        setLoadingState('loading');
        await new Promise(resolve => setTimeout(resolve, 500));
        setSimulationResults(cachedResult);
        setLoadingState('success');
        setLastSimulatedPicks(playerPicks);
        setLastSimulatedWinners([...knownWinners]);
        setLastSimulatedConfig(currentConfigString);
        setChangesMade(false);
        return;
      }

      setLoadingState('loading');
      trackEvent('run_simulation', { sample_size: sampleSize });

      // Call API with configuration — use poolId to avoid sending large payload
      const result = await simulateTournament({
        ...(activePoolName ? { poolId: activePoolName } : { playerPicks }),
        knownWinners,
        sampleSize,
        roundNames,
        pointsPerRound
      });

      // Store in cache and set results
      simulationCache.set(cacheKey, result);
      setSimulationResults(result);
      setLoadingState('success');

      // Update last simulated values and reset changesMade flag
      setLastSimulatedPicks(playerPicks);
      setLastSimulatedWinners([...knownWinners]);
      setLastSimulatedConfig(currentConfigString);
      setChangesMade(false);
    } catch (error: any) {
      console.error('Simulation error:', error);
      if (error?.code === 'pro_required') {
        setShowProModal(true);
        setLoadingState('idle');
      } else {
        setErrorMessage(error instanceof Error ? error.message : 'Unknown error occurred');
        setLoadingState('error');
      }
    }
  };

  // Determine if the simulate button should be disabled
  const isSimulateDisabled =
    loadingState === 'loading' ||
    !playerPicks.trim() ||
    bracketComplete ||
    !changesMade;

  return (
    <div className="my-5">
      <div className="flex items-center mb-2">
        <h2 className="text-xl font-semibold text-foreground">Simulation</h2>
        <InfoTooltip
          content={
            <>
              <p>Click simulate to run an analysis of all remaining games in your current scenario and see which player is most likely to win.</p>
              <br />
              <p>Games with known winners: {actualKnownWinnersCount}</p>
            </>
          }
        />
      </div>

      <Card>
        <CardContent className="p-5">
          <div className="flex flex-col items-center gap-5 mb-4">
            <Button
              variant="default"
              size="lg"
              onClick={handleSimulate}
              disabled={isPro ? isSimulateDisabled : false}
              className="w-[260px]"
            >
              {loadingState === 'loading' ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Simulating...
                </>
              ) : (
                <>
                  Simulate Tournament
                  <span className="ml-1.5 rounded bg-[#1c1917] px-1.5 py-0.5 text-[10px] font-bold leading-none text-[#faf8f5]">PRO</span>
                </>
              )}
            </Button>

            {playerPicks.trim() && actualKnownWinnersCount > 0 && (
              <Button
                variant="outline"
                size="lg"
                onClick={() => isPro ? setShowChancesDialog(true) : setShowProModal(true)}
                className="w-[260px]"
              >
                Win Probability Timeline
                <span className="ml-1.5 rounded bg-[#1c1917] px-1.5 py-0.5 text-[10px] font-bold leading-none text-[#faf8f5]">PRO</span>
              </Button>
            )}

            <div className="text-muted-foreground text-center">
              {bracketComplete ? (
                <p className="font-bold text-success">Tournament Complete!</p>
              ) : (
                actualKnownWinnersCount > 0 && (
                  <p></p>
                )
              )}
            </div>
          </div>

          {/* Note for user about resimulation */}
          {simulationResults && !changesMade && !bracketComplete && (
            <div className="text-sm text-muted-foreground italic mt-1 text-center">
              To run another simulation, make changes to the bracket.
            </div>
          )}

          {/* Scenario information - moved from ResultsDisplay */}
          {simulationResults && (
            <div className="text-sm text-muted-foreground mt-2.5 text-center">
              <div>
                <strong>Total possible scenarios:<br/></strong> {simulationResults.totalPossibleScenarios.toLocaleString()}
                {simulationResults.totalPossibleScenarios > 1000000000 && (
                  <InfoTooltip content={
                    <div>
                      <p><b>How do I say this?</b></p>
                      {formatLargeNumber(simulationResults.totalPossibleScenarios, true)
                        .split('\n')
                        .map((line, index) => (
                          <p key={index}>{line}</p>
                        ))}
                    </div>
                  } />
                )}

                {/* Ties information - only show if ties are possible */}
                {simulationResults.tiesPossible > 0 && (
                  <div className="mt-2 text-muted-foreground text-[13px]">
                    <b>Ties possible!</b> {Math.round(simulationResults.tiesPercentage)}% of the scenarios result in ties between at least two players. In Paths, ties are counted as wins for each player. In Probability, ties are coin flips among tied players.
                    {simulationResults.tiesPossible > simulationResults.totalPossibleScenarios && (
                      <span> It's possible for this number to be over 100% because of three- and four-way ties which get counted three or four times, etc.</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {loadingState === 'loading' && (
            <div className="flex items-center gap-2.5 mt-2.5">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
              <p className="text-foreground">Running simulation... This may take a moment.</p>
            </div>
          )}

          {errorMessage && (
            <div className="text-destructive mt-2.5 p-2.5 bg-destructive/10 border border-destructive/30 rounded-md">
              {errorMessage}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Display results if available */}
      {simulationResults && (
        <div>
          <div className="flex items-center mt-4">
            <h3 className="text-lg font-semibold text-foreground">Results</h3>
          </div>
          <ResultsDisplay
            results={simulationResults}
            onPlayerSelect={onPlayerSelect}
            bracketComplete={bracketComplete}
            pinnedPlayers={pinnedPlayers}
            onPinnedPlayersChange={onPinnedPlayersChange}
            pageSize={pageSize}
          />
        </div>
      )}

      <ProUpgradeModal isOpen={showProModal} onClose={() => setShowProModal(false)} />
      <ChancesOverTime
        isOpen={showChancesDialog}
        onClose={() => setShowChancesDialog(false)}
        playerPicks={playerPicks}
        activePoolName={activePoolName}
        pinnedPlayers={pinnedPlayers}
      />
    </div>
  );
};

export default SimulationSection;
